import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTable, usePagination, useSortBy, useFilters, useGlobalFilter } from 'react-table';
import { Appointment } from '../../../../../v2/services/Appointment';
import { format } from 'date-fns';

export default function PatientRecord() {
  const { id } = useParams();
  const [patientData, setPatientData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const getRecords = async () => {
    try {
      const res = await Appointment.patientRecords({
        doctorId: id,
        // status: 'pending',
        page: 1,
        limit: 50
      });

      const formattedData = res?.data?.records?.map((record) => ({
        col1: record.userFullName,
        col2: format(new Date(record.appDate), 'dd-MM-yyyy'),
        col3: record.appTime,
        col4: record.motiveName,
        col5: record.doctorNotes || 'N/A',
        col6: record.userNotes || 'N/A',
        col7: record.diseases?.map(d => d.name).join(', ') || 'None',
        col8: record.statusName || 'N/A',


      })) || [];

      setPatientData(formattedData);
    } catch (error) {
      console.error('Failed to load patient records:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRecords();
  }, [id]);

  const patientColumns = useMemo(() => [
    { Header: 'Paciente', accessor: 'col1' },
    { Header: 'Fecha', accessor: 'col2' },
    { Header: 'Hora', accessor: 'col3' },
    { Header: 'Motivo', accessor: 'col4' },
    { Header: 'Notas del Doctor', accessor: 'col5' },
    { Header: 'Notas del Usuario', accessor: 'col6' },
    { Header: 'Enfermedades', accessor: 'col7' },
    { Header: 'Estado', accessor: 'col8' }
  ], []);

  const tableInstance = useTable(
    { columns: patientColumns, data: patientData },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // use 'page' instead of 'rows' for pagination
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: { pageIndex }
  } = tableInstance;

  if (isLoading) return <div className="p-4">Cargando...</div>;

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Historial del Paciente</h2>

      {patientData.length === 0 ? (
        <p>No hay registros disponibles.</p>
      ) : (
        <>
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-[24px] table-top">
            <table
              {...getTableProps()}
              className="min-w-full divide-y divide-gray-300  table-gredient  docter-table"
            >
              <thead className="bg-gray-50">
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                    {headerGroup.headers.map(column => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 relative"
                        key={column.id}
                      >
                        {column.render('Header')}
                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={row.id} className="hover:bg-gray-50">
                      {row.cells.map(cell => (
                        <td
                          {...cell.getCellProps()}
                          className="px-4 py-2 border"
                          key={cell.column.id}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className={`px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 ${!canPreviousPage && 'opacity-50 cursor-not-allowed'}`}
            >
              Anterior
            </button>
            <span className="text-sm">Página {pageIndex + 1}</span>
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className={`px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 ${!canNextPage && 'opacity-50 cursor-not-allowed'}`}
            >
              Siguiente
            </button>
          </div>
        </>
      )}
    </div>
  );
}
